import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@duck/ui'
import { EnvelopeIcon } from '@heroicons/react/24/outline'
import { SignupForm } from './SignupForm'
import { GoogleSignInButton } from '../LoginDialog/GoogleSignInButton'
import { LinkedInSignInButton } from '../LoginDialog/LinkedInSignInButton'
import { useTar } from '@/modules'
import { GoogleLoadingView, LinkedInLoadingView } from '../AuthComponents/AuthSocialLoadingViews'

export const SignupDialog = () => {
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [isLinkedInLoading, setIsLinkedInLoading] = useState(false)
  const [isStep2, setIsStep2] = useState(false)
  const [showGoogleExpanded, setShowGoogleExpanded] = useState(false)
  const [showLinkedInExpanded, setShowLinkedInExpanded] = useState(false)
  const {
    isSignupDialogOpen,
    closeSignupDialog,
    openLoginDialog,
    isRegistering,
    signInWithGoogle,
    signInWithLinkedIn,
    error,
    invitationData
  } = useAuth()
  const { t } = useTar()

  const handleStepChange = useCallback((step: 1 | 2) => {
    setIsStep2(step === 2)
  }, [])

  // Reset step when dialog closes
  useEffect(() => {
    if (!isSignupDialogOpen) {
      setIsStep2(false)
      setShowGoogleExpanded(false)
      setShowLinkedInExpanded(false)
    }
  }, [isSignupDialogOpen])

  const handleGoogleSignUp = () => {
    if (isGoogleLoading) return

    // If no invitation, proceed directly
    if (!invitationData?.email) {
      handleGoogleContinue()
      return
    }

    // First click: show expanded view, close LinkedIn if open
    if (!showGoogleExpanded) {
      setShowLinkedInExpanded(false)
      setShowGoogleExpanded(true)
      return
    }
  }

  const handleGoogleContinue = async () => {
    if (isGoogleLoading) return
    try {
      setIsGoogleLoading(true)
      if (signInWithGoogle) {
        await signInWithGoogle()
      }
    } catch (error) {
      console.error('Google sign-up error:', error)
      setIsGoogleLoading(false)
    }
  }

  const handleLinkedInSignUp = () => {
    if (isLinkedInLoading) return

    // If no invitation, proceed directly
    if (!invitationData?.email) {
      handleLinkedInContinue()
      return
    }

    // First click: show expanded view, close Google if open
    if (!showLinkedInExpanded) {
      setShowGoogleExpanded(false)
      setShowLinkedInExpanded(true)
      return
    }
  }

  const handleLinkedInContinue = async () => {
    if (isLinkedInLoading) return
    try {
      setIsLinkedInLoading(true)
      if (signInWithLinkedIn) {
        await signInWithLinkedIn()
      }
    } catch (error) {
      console.error('LinkedIn sign-up error:', error)
      setIsLinkedInLoading(false)
    }
  }

  const isAnyLoading = isGoogleLoading || isLinkedInLoading || isRegistering

  return (
    <Dialog
      open={isSignupDialogOpen}
      onOpenChange={(open) => !isAnyLoading && !open && closeSignupDialog()}
    >
      <DialogContent
        className="sm:max-w-md rounded-none sm:rounded-lg sm:min-w-[480px]"
        onPointerDownOutside={(e) => isAnyLoading && e.preventDefault()}
        onEscapeKeyDown={(e) => isAnyLoading && e.preventDefault()}
        onInteractOutside={(e) => isAnyLoading && e.preventDefault()}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-lg sm:text-xl">
            {t('common.signup')}
          </DialogTitle>
        </DialogHeader>

        {isLinkedInLoading ? (
          <LinkedInLoadingView />
        ) : isGoogleLoading ? (
          <GoogleLoadingView />
        ) : (
          <div className="space-y-6">
            {!isStep2 && (
              <>
                <div className="text-center space-y-2">
                  <h3 className="text-lg font-medium text-foreground">
                    {t('auth.welcomeToAppName')}
                  </h3>
                </div>

                {invitationData && (
                  <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                    <div className="flex items-center">
                      <EnvelopeIcon className="w-5 h-5 text-success mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-primary">
                          {invitationData.inviterName ? t('common.signupWithInvitationFromX', { inviter: invitationData.inviterName }) : t('common.signupWithInvitation')}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex flex-col space-y-3">
                  {/* LinkedIn Sign-Up Button */}
                  <div className="flex flex-col">
                    <LinkedInSignInButton
                      onClick={handleLinkedInSignUp}
                      isLoading={isLinkedInLoading}
                      isDisabled={isAnyLoading}
                    />
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${showLinkedInExpanded && invitationData?.email
                        ? 'max-h-96 opacity-100 mt-2'
                        : 'max-h-0 opacity-0'
                        }`}
                    >
                      <div className="bg-card border border-border rounded-lg p-4 flex flex-col">
                        <p className="text-sm text-foreground mb-3">
                          {t('auth.registrationViaLinkedInMustBeWithAccountLinkedToEmail')}{' '}
                          <span className="font-mono font-semibold text-accent">{invitationData?.email}</span>
                        </p>
                        <button
                          onClick={handleLinkedInContinue}
                          disabled={isAnyLoading}
                          className="text-sm text-accent hover:text-foreground transition-colors font-medium self-center"
                        >
                          {t('common.continue')}
                        </button>
                      </div>
                    </div>
                  </div>
                  {/* Google Sign-Up Button */}
                  <div className="flex flex-col">
                    <GoogleSignInButton
                      onClick={handleGoogleSignUp}
                      isLoading={isGoogleLoading}
                      isDisabled={isAnyLoading}
                    />
                    <div
                      className={`overflow-hidden transition-all duration-300 ease-in-out ${showGoogleExpanded && invitationData?.email
                        ? 'max-h-96 opacity-100 mt-2'
                        : 'max-h-0 opacity-0'
                        }`}
                    >
                      <div className="bg-card border border-border rounded-lg p-4 flex flex-col">
                        <p className="text-sm text-foreground mb-3">
                          {t('auth.registrationViaGoogleMustBeWithAccountLinkedToEmail')}{' '}
                          <span className="font-mono font-semibold text-accent">{invitationData?.email}</span>
                        </p>
                        <button
                          onClick={handleGoogleContinue}
                          disabled={isAnyLoading}
                          className="text-sm text-accent hover:text-foreground transition-colors font-medium self-center"
                        >
                          {t('common.continue')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-border"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-card text-muted">{t('auth.signupWithEmail')}</span>
                  </div>
                </div>
              </>
            )}

            {/* Error Message - Show in both steps */}
            {error && (
              <div className="bg-error/10 border border-error/20 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-error mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <h4 className="text-sm font-medium text-error">
                      {t('auth.signupError')}
                    </h4>
                    <p className="text-sm text-error mt-1">
                      {error}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Email Sign-Up Form */}
            <SignupForm onStepChange={handleStepChange} />

            {/* Footer - Only show in step 1 */}
            {!isStep2 && (
              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  {t('auth.alreadyRegistered')}{' '}
                  <button
                    onClick={openLoginDialog}
                    className={`font-medium transition-colors
                    ${isAnyLoading
                        ? 'text-muted cursor-not-allowed'
                        : 'text-accent hover:text-foreground'
                      }`}
                    disabled={isAnyLoading}
                  >
                    {t('common.goToLogin')}
                  </button>
                </p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
} 